import type { Objective1 } from '@mm-shared/types/Objective1';
import type { DependencyRecord } from '@mm-shared/dependencies';
import { getDependencyId } from '@mm-shared/dependencies';
import { ObjectiveDisplayMode } from '../../store/map3UISlice';

/**
 * Filters objectives based on display mode and dependency visibility
 * @param objectives - Array of objectives to filter
 * @param displayMode - Current objective display mode
 * @param dependencyRecords - All dependency records
 * @param visibleDependencies - Array of dependencies to display visibly
 * @returns Filtered array of objectives
 */
export function filterObjectivesByDisplayMode(
  objectives: Objective1[],
  displayMode: ObjectiveDisplayMode,
  dependencyRecords: DependencyRecord[],
  visibleDependencies: Array<{ depId: string; visible: boolean }>,
): Objective1[] {
  switch (displayMode) {
    case 'show_all':
      // Show all objectives regardless of dependencies
      return objectives;

    case 'show_with_dependencies':
    case 'show_all_relatives':
      // Filter to only show objectives that have visible dependencies
      return objectives.filter((objective) =>
        hasVisibleDependencies(objective, dependencyRecords, visibleDependencies),
      );

    default:
      return objectives;
  }
}

/**
 * Finds all dependencies involving an objective's results
 * @param objective - The objective to check
 * @param dependencyRecords - All dependency records
 * @returns Array of dependencies involving the objective
 */
export function getObjectiveDependencies(
  objective: Objective1,
  dependencyRecords: DependencyRecord[],
): DependencyRecord[] {
  return dependencyRecords.filter((dep) => {
    const isSource =
      objective.results?.some((result) => result.uuid === dep.sourceResult.uuid) || false;
    const isTarget =
      objective.results?.some((result) => result.uuid === dep.targetResult.uuid) || false;
    return isSource || isTarget;
  });
}

/**
 * Checks if a dependency is visible
 * @param dependency - The dependency to check
 * @param visibleDependencies - Array of visible dependency states
 * @returns True if the dependency is marked as visible
 */
export function isDependencyVisible(
  dependency: DependencyRecord,
  visibleDependencies: Array<{ depId: string; visible: boolean }>,
): boolean {
  const depId = getDependencyId(dependency);
  const visibilityState = visibleDependencies.find((vDep) => vDep.depId === depId);
  return visibilityState?.visible === true;
}

/**
 * Determines if an objective has any visible dependencies
 * @param objective - The objective to check
 * @param dependencyRecords - All dependency records
 * @param visibleDependencies - Array of visible dependency states
 * @returns True if the objective has at least one visible dependency
 */
export function hasVisibleDependencies(
  objective: Objective1,
  dependencyRecords: DependencyRecord[],
  visibleDependencies: Array<{ depId: string; visible: boolean }>,
): boolean {
  const objectiveDependencies = getObjectiveDependencies(objective, dependencyRecords);
  return objectiveDependencies.some((dep) => isDependencyVisible(dep, visibleDependencies));
}

/**
 * Filters objectives for a specific map based on display mode
 * Helper function to filter objectives for a single milestone map
 * @param mapObjectives - Objectives for a specific map
 * @param displayMode - Current objective display mode
 * @param dependencyRecords - All dependency records
 * @param visibleDependencies - Array of visible dependency states
 * @returns Filtered objectives for the map
 */
export function filterMapObjectives(
  mapObjectives: Objective1[],
  displayMode: ObjectiveDisplayMode,
  dependencyRecords: DependencyRecord[],
  visibleDependencies: Array<{ depId: string; visible: boolean }>,
): Objective1[] {
  return filterObjectivesByDisplayMode(
    mapObjectives,
    displayMode,
    dependencyRecords,
    visibleDependencies,
  );
}
